import { auth, storage, EmailAuthProvider, getSocialProviderFromId } from '../db'
import { AUTH_LOGIN_SUCCESSFUL, AUTH_LOGOUT, AUTH_RELOAD_VERIFICATION, LOADING, STOP_LOADING, AUTH_PROFILE_PIC, MODAL_SHOW } from '../mutation-types';

import { Module } from "vuex";
import store, { RootState } from '..';

import FormModal from '@/components/modals/ModalForm.vue'
import { EventMap } from './modal';

import i18n from '@/i18n'
import router from '@/router';
import { TranslateResult } from 'vue-i18n';


export interface AuthState {
  user?: firebase.User;
  userPicOverride: string;
  verified: boolean;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

async function loginSocial(commit: Function, info: string | TranslateResult , provider: firebase.auth.AuthProvider): Promise<firebase.User | null> {
  return new Promise<firebase.User | null>((resolve) => {
    const props = {
      fields: []
    }

    const events: EventMap = {
      confirm: async () => {
        try {
          const result = await auth.signInWithPopup(provider)
          resolve(result.user)
        } catch(e) {
          resolve(null)
        }
      },
      cancel: () => {
        resolve(null)
      }
    }

    const payload = { component: FormModal, props, events, info }

    commit('modal/' + MODAL_SHOW, payload, { root: true })
  })
}

async function promptPassword(commit: Function, info: string): Promise<string | void> {
  return new Promise<string | void>((resolve) => {
    const props = {
      fields: [
        {
          name: 'password',
          type: 'password',
          props: {
            autocomplete: 'current-password',
            placeholder: i18n.t('form.password.placeholder')
          }
        }
      ]
    }

    const events: EventMap = {
      confirm: (data: { password: string }) => {
        resolve(data.password)
      },
      cancel: () => {
        resolve()
      }
    }

    const payload = { component: FormModal, props, events, info }

    commit('modal/' + MODAL_SHOW, payload, { root: true })
  })
}


export const authInit = new Promise(resolve => {
  
  auth.onAuthStateChanged(user => {
    if(user) {
      store.dispatch('login', user)
    }
    resolve(user)
  })
})


function resize(file: File, max: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if(!ctx) {
      reject('could not create canvas context')
      return
    } 
    
    ctx.imageSmoothingEnabled = true
    img.onload = () => {
      if (max < img.height * img.width) {
        const sq = Math.sqrt(img.width / img.height)
        const smax = Math.sqrt(max)
        canvas.width = sq * smax
        canvas.height = smax / sq
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
        canvas.toBlob(result => {

          if(result) {
            resolve(result)
          } else {
            reject('could not resize')
          }

        }, 'image/jpeg', 95)
      } else {
        resolve(file)
      }
    }
    img.src = URL.createObjectURL(file)
  })
}



const AuthModule: Module<AuthState,RootState> = {
  state: {
    user: undefined,
    userPicOverride: '',
    verified: false,
  },
  mutations: {
    [AUTH_PROFILE_PIC]: (state, url) => {
      if(state.user) {
        state.userPicOverride = url
      }
    },
    [AUTH_LOGOUT]: (state) => {
      state.user = undefined
      state.userPicOverride = '',
      state.verified = false
    },
    [AUTH_LOGIN_SUCCESSFUL]: (state, user: firebase.User) => {
      state.user = user
      state.verified = user.emailVerified
    },
    [AUTH_RELOAD_VERIFICATION]: (state, verification) => {
      state.verified = verification
    }
  },
  actions: {
    async sendEmailVerification({state, commit}) {
      commit(LOADING)
      
      if (state.user && !state.user.emailVerified) {
        await state.user.sendEmailVerification()
      }
      commit(STOP_LOADING)
    },
    async registerEmailUser({ commit }, { email, password }: LoginCredentials) {
      commit(LOADING)
      let err = null
      try {
        const creds = await auth.createUserWithEmailAndPassword(email, password)
        if(!creds.user?.emailVerified) {
          await creds.user?.sendEmailVerification()
        }
      } catch (e) {
        err = e
      }
      commit(STOP_LOADING)
      if(err) throw err
    },
    login({commit}, user) {
      commit(AUTH_LOGIN_SUCCESSFUL, user);
    },
    async logout({commit}) {
      commit(LOADING)
      await auth.signOut()
      commit(AUTH_LOGOUT)
      commit(STOP_LOADING)
      router.push({ name: 'Login' })
    },
    async loginEmail({commit}, {email, password}: LoginCredentials) {
      commit(LOADING)
      let err = null
      try {
        await auth.signInWithEmailAndPassword(email, password)
      } catch(e) {
        err = e
      }
      commit(STOP_LOADING)
      if (err) throw err;
    },
    async loginSocial({commit}, providerId) {
      const provider = new (getSocialProviderFromId(providerId))
      const providerName = i18n.t('social.' + providerId)

      try {
        const result = await auth.signInWithPopup(provider)
        if(result.user && !result.user.emailVerified) {
          await result.user.sendEmailVerification()
        }
        //await auth.signInWithRedirect(provider)
      } catch (error) {
        if (error.code == 'auth/account-exists-with-different-credential') {
          const { email, credential }: { email: string; credential: firebase.auth.AuthCredential} = error

          const methods = await auth.fetchSignInMethodsForEmail(email)
          if (methods[0] == 'password') {
            const password = await promptPassword(commit, i18n.t('info.password-for-login', { email, provider: providerName }).toString())
            if(password) {
              const result = await auth.signInWithEmailAndPassword(email, password);
              if(result.user) {
                await result.user.linkWithCredential(credential)
              }
            }
          } else if (methods[0].indexOf('facebook') >= 0 || methods[0].indexOf('google') >= 0 || methods[0].indexOf('twitter') >= 0) {
            const oldProvider = new (getSocialProviderFromId(methods[0]))
            if (methods[0].indexOf('google') >= 0) {
              oldProvider.setCustomParameters({ "login_hint": email })
            }
            
            const user = await loginSocial(commit, i18n.t('login.will-be-linked-with-' + oldProvider.providerId), oldProvider)

            if(user) {
              user.linkWithCredential(credential)
            }
          }
        }
      }
    },
    async reloadUser({commit, state}) {
      if(state.user) {
        await state.user.reload()
        commit(AUTH_RELOAD_VERIFICATION, state.user.emailVerified);
      }
    },
    async updateProfile({commit, state}, userProfile) {
      if (state.user) {
        commit(LOADING)
        await state.user.updateProfile(userProfile)
        commit(STOP_LOADING)
      }
    },
    async uploadUserPic({commit, state}, pic: File) {
      if (state.user) {
        commit(LOADING)
        const resized = await resize(pic, 300*300)
        commit(AUTH_PROFILE_PIC, URL.createObjectURL(resized))
        
        const userId = state.user.uid;
        const ref = storage.ref(`${userId}/profilePicture/${userId}.jpg`)
        await ref.put(resized)
        
        commit(STOP_LOADING)
      }
    },
    async recoverPassword({commit}, email: string) {
      commit(LOADING)
      try {
        await auth.sendPasswordResetEmail(email)
      } finally {
        commit(STOP_LOADING)
      }
    },
    async reauthenticate({state, commit}) {
      const password = await new Promise<string | void>((resolve) => {
        const props = {
          fields: [
            {
              name: 'password',
              type: 'password',
              props: {
                autocomplete: 'current-password',
                placeholder: i18n.t('form.current-password.placeholder')
              }
            }
          ]
        }

        const events: EventMap = {
          confirm: (data: { password: string }) => {
            resolve(data.password)
          },
          cancel: () => {
            resolve()
          }
        }
        const payload = { component: FormModal, props, events, info: i18n.t('info.password-for-reauth') }

        commit('modal/' + MODAL_SHOW, payload, { root: true })
      })

      if(password && state.user && state.user.email) {
        await state.user.reauthenticateWithCredential(EmailAuthProvider.credential(state.user.email, password))
      } else {
        throw 'auth/did-not-authenticate'
      }
    },
    async changeEmail({commit, dispatch, state}, email) {
      if(state.user) {
        commit(LOADING)
        try {
          await state.user.updateEmail(email)
          await dispatch('reloadUser')
          await dispatch('sendEmailVerification')
        } catch (e) {
          if (e.code == 'auth/requires-recent-login') {
            await dispatch('reauthenticate')
            dispatch('changeEmail', email)
          } else {
            throw e
          }
        } finally {
          commit(STOP_LOADING)
        }
      }
    },
    async changePassword({commit, dispatch, state}, newPassword) {
      if (state.user && state.user.email) {
        commit(LOADING)
        try {
          await state.user.updatePassword(newPassword)
        } catch(e) {
          if(e.code == 'auth/requires-recent-login') {
            await dispatch('reauthenticate')
            dispatch('changePassword', newPassword)
          } else {
            throw e
          }
        } finally {
          commit(STOP_LOADING)
        }
      }
    }
  },
  getters: {
    isLoggedIn: (state) => {
      return state.user !== undefined && state.user !== null
    },
    isVerified: (state) => {
      return state.verified
    },
    user: (state) => {
      return state.user
    },
    userPic: (state) => {
      if(state.userPicOverride.length > 0) {
        return state.userPicOverride
      } else if(state.user) {
        return state.user.photoURL
      } else {
        return false
      }
    },

    authInit: () => {
      return authInit
    }
  }
}

export default AuthModule